export type MsgType = "SMS" | "LMS" | "MMS";
export type SendStatus = "success" | "fail" | "pending" | "scheduled";

export interface RecipientDetail {
  name?: string;
  phone: string;
  status: "success" | "fail" | "pending";
  batchIndex?: number;
  failReason?: string;
}

export interface HistoryItem {
  id: string;
  type: MsgType;
  subject?: string;
  message: string;
  hasImage?: boolean;
  recipients: RecipientDetail[];
  senderNumber: string;
  status: SendStatus;
  sentAt: string;
  scheduledAt?: string;
  successCount: number;
  failCount: number;
  cost: number;
  totalCount: number;
  refundAmount?: number;
}

// DB 원본 타입
export interface SendHistoryRow {
  id: string;
  user_id: string;
  type: MsgType;
  subject: string | null;
  message: string;
  has_image: boolean;
  sender_number: string;
  status: SendStatus;
  total_count: number;
  success_count: number;
  fail_count: number;
  cost: number;
  scheduled_at: string | null;
  sent_at: string;
  created_at: string;
  send_recipients?: SendRecipientRow[];
}

export interface SendRecipientRow {
  id: string;
  history_id: string;
  name: string | null;
  phone: string;
  status: "success" | "fail" | "pending";
  batch_index?: number;
  fail_reason?: string | null;
}

// DB row → HistoryItem 변환
export function rowToHistoryItem(row: SendHistoryRow, refundMap?: Record<string, number>): HistoryItem {
  const recipients: RecipientDetail[] = (row.send_recipients ?? []).map((r) => ({
    name: r.name ?? undefined,
    phone: r.phone,
    status: r.status === "failed" ? "fail" : r.status,
    batchIndex: r.batch_index,
    failReason: r.fail_reason ?? undefined,
  }));
  return {
    id: row.id,
    type: row.type,
    subject: row.subject ?? undefined,
    message: row.message,
    hasImage: row.has_image,
    recipients,
    senderNumber: row.sender_number,
    status: (row.status === "failed" ? "fail" : row.status) as SendStatus,
    sentAt: formatKST(row.sent_at),
    scheduledAt: row.scheduled_at ? formatKST(row.scheduled_at) : undefined,
    successCount: row.success_count,
    failCount: row.fail_count,
    cost: row.cost,
    totalCount: row.total_count,
    refundAmount: refundMap?.[row.id],
  };
}

function formatKST(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${y}.${mo}.${day} ${h}:${mi}`;
}

export const STATUS_CONFIG: Record<SendStatus, { label: string; className: string; dotClass: string; icon: string }> = {
  success: {
    label: "성공",
    className: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20",
    dotClass: "bg-emerald-400",
    icon: "ri-checkbox-circle-line",
  },
  fail: {
    label: "실패",
    className: "bg-red-500/15 text-red-400 border border-red-500/20",
    dotClass: "bg-red-400",
    icon: "ri-close-circle-line",
  },
  pending: {
    label: "처리 중",
    className: "bg-amber-500/15 text-amber-400 border border-amber-500/20",
    dotClass: "bg-amber-400",
    icon: "ri-loader-4-line",
  },
  scheduled: {
    label: "예약",
    className: "bg-violet-500/15 text-violet-400 border border-violet-500/20",
    dotClass: "bg-violet-400",
    icon: "ri-calendar-schedule-line",
  },
};

export const TYPE_CONFIG: Record<MsgType, { className: string; price: number }> = {
  SMS: { className: "bg-violet-500/20 text-violet-300", price: 20 },
  LMS: { className: "bg-pink-500/20 text-pink-300", price: 50 },
  MMS: { className: "bg-amber-500/20 text-amber-300", price: 100 },
};

export const MOCK_HISTORY: HistoryItem[] = [
  {
    id: "h1",
    type: "SMS",
    message: "[Signal SMS] 인증번호는 [482910]입니다. 3분 내에 입력해주세요.",
    recipients: [{ phone: "010-1234-5678", name: "김민준", status: "success" }],
    senderNumber: "02-1234-5678",
    status: "success",
    sentAt: "2026.05.13 14:32",
    successCount: 1,
    failCount: 0,
    cost: 20,
  },
  {
    id: "h2",
    type: "LMS",
    subject: "5월 특별 프로모션 안내",
    message: "안녕하세요, 5월 특별 프로모션 안내드립니다. 이번 달 한정으로 전 상품 20% 할인 이벤트를 진행합니다. 5월 31일까지 구매 시 추가 포인트 적립 혜택도 드립니다. 많은 이용 부탁드립니다.",
    recipients: [
      { phone: "010-1234-5678", name: "김민준", status: "success" },
      { phone: "010-2345-6789", name: "이서연", status: "success" },
      { phone: "010-3456-7890", name: "박지호", status: "success" },
      { phone: "010-4567-8901", name: "최수아", status: "fail" },
      { phone: "010-5678-9012", name: "정도윤", status: "success" },
      { phone: "010-6789-0123", name: "강하은", status: "success" },
      { phone: "010-7890-1234", name: "윤시우", status: "success" },
      { phone: "010-8901-2345", name: "임나연", status: "success" },
    ],
    senderNumber: "02-1234-5678",
    status: "success",
    sentAt: "2026.05.13 11:00",
    successCount: 7,
    failCount: 1,
    cost: 400,
  },
  {
    id: "h3",
    type: "MMS",
    subject: "5월 신상품 출시 안내",
    message: "안녕하세요! 이번 달 새롭게 출시된 신상품을 소개합니다. 첨부 이미지를 확인해 주세요. 선착순 100명 구매 시 특별 할인 혜택이 제공됩니다.",
    hasImage: true,
    recipients: [
      { phone: "010-1111-2222", name: "VIP 고객 A", status: "success" },
      { phone: "010-2222-3333", name: "VIP 고객 B", status: "success" },
      { phone: "010-3333-4444", name: "VIP 고객 C", status: "success" },
      { phone: "010-4444-5555", name: "VIP 고객 D", status: "fail" },
      { phone: "010-5555-6666", name: "VIP 고객 E", status: "success" },
    ],
    senderNumber: "010-9876-5432",
    status: "success",
    sentAt: "2026.05.12 09:15",
    successCount: 4,
    failCount: 1,
    cost: 500,
  },
  {
    id: "h4",
    type: "SMS",
    message: "[Signal SMS] 주문이 완료되었습니다. 주문번호: ORD-20260512-0042",
    recipients: [{ phone: "010-9876-5432", name: "박철수", status: "fail" }],
    senderNumber: "02-1234-5678",
    status: "fail",
    sentAt: "2026.05.12 08:44",
    successCount: 0,
    failCount: 1,
    cost: 0,
  },
  {
    id: "h5",
    type: "LMS",
    subject: "5월 정기 점검 안내",
    message: "안녕하세요! 6월 정기 점검 안내입니다. 5월 20일 오전 2시~4시 서비스 점검이 예정되어 있습니다. 이용에 불편을 드려 죄송합니다.",
    recipients: Array.from({ length: 12 }, (_, i) => ({
      phone: `010-${String(1000 + i * 111).padStart(4, "0")}-${String(2000 + i * 222).padStart(4, "0")}`,
      name: `고객 ${i + 1}`,
      status: "pending" as const,
    })),
    senderNumber: "02-1234-5678",
    status: "scheduled",
    sentAt: "2026.05.20 02:00",
    scheduledAt: "2026.05.20 02:00",
    successCount: 0,
    failCount: 0,
    cost: 600,
  },
  {
    id: "h6",
    type: "SMS",
    message: "[Signal SMS] 결제가 완료되었습니다. 금액: 48,500원",
    recipients: [{ phone: "010-5555-1234", name: "이지은", status: "success" }],
    senderNumber: "02-1234-5678",
    status: "success",
    sentAt: "2026.05.11 16:20",
    successCount: 1,
    failCount: 0,
    cost: 20,
  },
  {
    id: "h7",
    type: "SMS",
    message: "[Signal SMS] 가입을 환영합니다! 첫 충전 시 10% 보너스 크레딧을 드립니다.",
    recipients: Array.from({ length: 8 }, (_, i) => ({
      phone: `010-${String(7000 + i * 111).padStart(4, "0")}-${String(8000 + i * 222).padStart(4, "0")}`,
      name: `신규 가입자 ${i + 1}`,
      status: "success" as const,
    })),
    senderNumber: "02-1234-5678",
    status: "success",
    sentAt: "2026.05.11 10:05",
    successCount: 8,
    failCount: 0,
    cost: 160,
  },
  {
    id: "h8",
    type: "SMS",
    message: "[Signal SMS] 비밀번호 재설정 링크를 발송했습니다. 링크: https://signal.kr/reset/abc123",
    recipients: [{ phone: "010-3344-5566", name: "최영호", status: "success" }],
    senderNumber: "02-1234-5678",
    status: "success",
    sentAt: "2026.05.10 13:55",
    successCount: 1,
    failCount: 0,
    cost: 20,
  },
  {
    id: "h9",
    type: "LMS",
    subject: "4월 이용 내역 안내",
    message: "안녕하세요. 4월 한 달간 Signal SMS를 이용해 주셔서 감사합니다. 이번 달 총 발송 건수: 1,204건, 성공률: 98.2%, 사용 금액: ₩42,800입니다.",
    recipients: Array.from({ length: 5 }, (_, i) => ({
      phone: `010-${String(9000 + i * 111).padStart(4, "0")}-${String(1000 + i * 222).padStart(4, "0")}`,
      name: `관리자 ${i + 1}`,
      status: "success" as const,
    })),
    senderNumber: "02-1234-5678",
    status: "success",
    sentAt: "2026.05.01 09:00",
    successCount: 5,
    failCount: 0,
    cost: 250,
  },
  {
    id: "h10",
    type: "MMS",
    subject: "4월 이벤트 당첨 안내",
    message: "축하합니다! 4월 이벤트에 당첨되셨습니다. 첨부 이미지의 쿠폰을 사용하시면 다음 구매 시 20% 할인 혜택을 받으실 수 있습니다.",
    hasImage: true,
    recipients: [
      { phone: "010-1212-3434", name: "당첨자 A", status: "success" },
      { phone: "010-2323-4545", name: "당첨자 B", status: "success" },
      { phone: "010-3434-5656", name: "당첨자 C", status: "fail" },
    ],
    senderNumber: "010-9876-5432",
    status: "success",
    sentAt: "2026.04.30 15:00",
    successCount: 2,
    failCount: 1,
    cost: 300,
  },
];